// Industry landing pages — the organic-search surface for the niches we can
// actually serve. The strategy review is explicit that breadth kills a network:
// the first market to saturate is industrial suppliers and manufacturers in the
// German-speaking region, so that page leads and the rest support it.
//
// Every claim here must match what ships (see lib/feature-status.ts). These
// pages rank for buying-intent queries, so an overstatement here is the most
// expensive kind.

export type Industry = {
  slug: string;
  /** Short label for navigation and cards. */
  name: string;
  metaTitle: string;
  metaDescription: string;
  /** H1 tail, rendered in italics. */
  heroEmphasis: string;
  intro: string;
  /** The concrete situation, in their language — not ours. */
  painTitle: string;
  pains: string[];
  /** What the twin does about it. Each is a capability that exists today. */
  gains: { title: string; body: string }[];
  /** Questions their buyers actually ask an AI assistant. */
  queries: string[];
  faqs: { q: string; a: string }[];
};

export const INDUSTRIES: Industry[] = [
  {
    slug: "manufacturing",
    name: "Manufacturing & industrial suppliers",
    metaTitle: "AI Twin for manufacturers & suppliers | Reality Twin",
    metaDescription:
      "Buyers now research suppliers through AI assistants. Give your plant a verified AI Twin that answers on capacity, tolerances, certifications and lead times — and gets found by other companies' agents.",
    heroEmphasis: "answers before the first call",
    intro:
      "Purchasing departments increasingly shortlist suppliers through AI assistants before anyone picks up a phone. If your capabilities, certifications and capacities aren't machine-readable, you are simply not in that shortlist — however good your shop floor is.",
    painTitle: "What this costs a supplier today",
    pains: [
      "Your website was built for humans in 2016 and says almost nothing a model can quote: no tolerances, no batch sizes, no certifications in text.",
      "Every RFQ arrives as an email that someone has to qualify by hand, and most of them aren't a fit.",
      "Buyers ask the same twenty questions — materials, minimum order, lead time, ISO status — and your team answers them one by one.",
      "You only learn about a tender when it's already been sent to three competitors.",
    ],
    gains: [
      {
        title: "Answers the qualifying questions, with sources",
        body: "Your twin learns from your website and the documents you upload — spec sheets, certificates, capability lists — and answers on materials, tolerances, batch sizes, certifications and lead times, citing where each answer came from. Anything it doesn't know, it says so and hands to a human.",
      },
      {
        title: "Pre-qualifies the RFQ before it reaches your desk",
        body: "When a buyer states a need, the twin collects what matters — what, how many, by when, for which market — and hands your team a brief instead of a transcript: who they are, what they want, budget and timing, and the open points.",
      },
      {
        title: "Reachable by other companies' agents",
        body: "Your twin is discoverable inside ChatGPT and Claude and through the Agent Card, so a buyer's agent can evaluate fit directly. That is the channel where supplier shortlists are increasingly assembled.",
      },
      {
        title: "Verified identity, which purchasing actually checks",
        body: "Prove domain ownership and record your legal form and register number. Procurement's first question — is this a real company — is answered before a human is involved. Swiss and EU hosting under FADP and GDPR.",
      },
    ],
    queries: [
      "Which CNC suppliers in Switzerland handle small batch runs?",
      "Who can machine titanium to ±0.01 mm in the DACH region?",
      "Find an ISO 9001 certified contract manufacturer with capacity this quarter",
    ],
    faqs: [
      {
        q: "Our specifications live in PDFs, not on the website. Does that work?",
        a: "Yes — upload them. PDFs, DOCX, spreadsheets exported as CSV and plain text all become part of the twin's knowledge, and every answer cites the source document.",
      },
      {
        q: "We don't want to publish prices. Can the twin still be useful?",
        a: "Yes. You control what goes into the knowledge base. Most suppliers publish capabilities and certifications, keep pricing out, and let the twin collect the enquiry so a human can quote.",
      },
      {
        q: "Will it promise a delivery date we can't hold?",
        a: "No. By default the twin is not authorised to make binding commitments: it presents figures and timelines as indicative and defers to your team for written confirmation. Binding statements have to be explicitly enabled by the owner.",
      },
      {
        q: "How long does setup take?",
        a: "About a minute to point it at your website. Uploading documents and reviewing what it learned is an afternoon, not a project.",
      },
    ],
  },
  {
    slug: "agencies",
    name: "Agencies & studios",
    metaTitle: "AI Twin for agencies: a recurring service | Reality Twin",
    metaDescription:
      "Run an AI Twin for your own inbound, then offer it to every client as a recurring service. Multiple twins from one account, a Swiss company behind it, honest roadmap.",
    heroEmphasis: "a service line, not another tool",
    intro:
      "Agencies are asked the same question by every client right now: what do we do about AI search? Most answers are a report. A twin is something you can install, show and bill monthly.",
    painTitle: "The agency situation",
    pains: [
      "Clients ask about AI visibility and the honest answer today is usually an audit with no fix attached.",
      "Retainers are under pressure and one-off projects don't compound.",
      "You manage many client websites and need one place to run them, not one login per client.",
    ],
    gains: [
      {
        title: "Lead with a free finding, not a pitch",
        body: "Run Twin-Check on a prospect's website and show them exactly how AI models read their company today — score, findings and the concrete gaps. The finding is free; the fix is the product you install.",
      },
      {
        title: "Run several twins",
        body: "Create a twin per client from one account, each with its own knowledge base, personality and public profile. Conversations draw from one shared pool, so you're not juggling per-client quotas.",
      },
      {
        title: "Something to show in the room",
        body: "Build a twin from the client's own URL and ask it three questions live in the meeting. That demo does more than a deck, and it takes about a minute to prepare.",
      },
    ],
    queries: [
      "Which agency can make our company visible in ChatGPT?",
      "AI visibility audit for B2B websites",
      "How do we get cited by AI assistants?",
    ],
    faqs: [
      {
        q: "Can we resell this under our own brand?",
        a: "Not yet — a dedicated agency workspace with white-labelling is on the roadmap and honestly marked as planned. Today you can run multiple twins from one account and bill your clients for the service around them.",
      },
      {
        q: "What do we actually sell to a client?",
        a: "Setup and knowledge curation, then a monthly retainer for keeping the twin current, reviewing what it couldn't answer, and acting on the leads it captures.",
      },
      {
        q: "Do we need to be technical?",
        a: "No. Setup is a URL and a script tag. Verification can be done by DNS record, meta tag or file — whichever your client's setup allows.",
      },
    ],
  },
  {
    slug: "professional-services",
    name: "Professional services",
    metaTitle: "AI Twin for law & accounting firms | Reality Twin",
    metaDescription:
      "Answer prospect questions accurately and on the record, qualify enquiries before the first meeting, and keep every statement auditable. Swiss company, EU-hosted data, GDPR and FADP aligned.",
    heroEmphasis: "on the record, not off the cuff",
    intro:
      "In advisory work the first conversation is expensive and most of them don't convert. A twin handles the qualifying part accurately — and, unusually for AI, keeps a record of exactly what it said and on what basis.",
    painTitle: "Where the time goes",
    pains: [
      'Initial calls that end in "we\'re not the right fit" — after 45 minutes.',
      "The same scoping questions, asked by every prospect, answered by your most expensive people.",
      "A genuine worry that an AI would state something as fact that later has to be walked back.",
    ],
    gains: [
      {
        title: "Every statement is on the record",
        body: "The statement register keeps a timestamped, hash-anchored copy of every answer the twin gave, with the sources it was grounded on — exportable. That is the difference between an AI that talks and one a regulated firm can defend.",
      },
      {
        title: "Non-binding by default",
        body: "Without explicit authorisation the twin never promises fees, deadlines or outcomes. It gives indicative guidance and hands to a human for written confirmation. Commitment levels are recorded per statement.",
      },
      {
        title: "Qualifies before it books",
        body: "The twin collects the situation, the timeframe and the budget frame, then books the meeting into your calendar — with the brief attached, so the first call starts at minute five, not minute zero.",
      },
    ],
    queries: [
      "Which firm advises on Swiss data protection for SaaS?",
      "Find a tax advisor for cross-border e-commerce in the DACH region",
      "Employment law specialist for a 40-person company",
    ],
    faqs: [
      {
        q: "Can it give legal or tax advice?",
        a: "It shouldn't, and by default it won't: it answers from your published material, states clearly when something needs a professional assessment, and hands over. You set the refusal boundaries explicitly.",
      },
      {
        q: "Is client data safe?",
        a: "Your knowledge base is isolated per company and hosted in the European Union (Ireland), covered by the GDPR and the Swiss FADP, with a DPA available. Only put material in the knowledge base that you're comfortable publishing to prospects.",
      },
      {
        q: "Can we prove what the twin told a prospect?",
        a: "Yes. Every answer is logged with its timestamp, sources and commitment level, and the whole register can be exported.",
      },
    ],
  },
  {
    slug: "b2b-saas",
    name: "B2B SaaS",
    metaTitle: "AI Twin for B2B SaaS pipelines | Reality Twin",
    metaDescription:
      "Turn documentation traffic into qualified pipeline: grounded answers with sources, lead scoring, in-chat meeting booking, and a signed webhook into your CRM.",
    heroEmphasis: "docs traffic becomes pipeline",
    intro:
      "SaaS companies have the content problem solved and the conversion problem open: thousands of visitors read the docs, a handful ever talk to anyone. A twin turns reading into a conversation while intent is live.",
    painTitle: "The familiar gap",
    pains: [
      "High documentation traffic, low demo conversion.",
      "Support answers pre-sales questions that sales never hears about.",
      "Evaluators compare you against three competitors and you never find out what they asked.",
    ],
    gains: [
      {
        title: "Grounded answers, with citations",
        body: "The twin answers from your docs and site with sources attached, so an evaluator gets a precise answer instead of a marketing paragraph — and you can see which excerpts it used.",
      },
      {
        title: "Scores and routes the ones that matter",
        body: "Intent is scored per conversation, contact details are captured in-flow, and the lead lands in your CRM through a signed webhook with a handoff brief attached.",
      },
      {
        title: "You see what evaluators actually ask",
        body: "Questions the twin couldn't answer are reported weekly — the most honest content roadmap you'll get, written by your own market.",
      },
    ],
    queries: [
      "Which tool does X and integrates with Y?",
      "Alternatives to <competitor> for mid-market teams",
      "Does this platform support SSO and EU hosting?",
    ],
    faqs: [
      {
        q: "Does it connect to our CRM?",
        a: "Through a signed webhook, which reaches any CRM directly or via Zapier, Make or n8n. Native one-click HubSpot and Salesforce apps are on the roadmap and marked as planned, not shipped.",
      },
      {
        q: "Will it hallucinate about our product?",
        a: "It answers from your indexed material and is instructed to say plainly when it doesn't know, then hand over. The weekly gap report shows you exactly where it had to.",
      },
      {
        q: "Can we put it on the docs and the marketing site?",
        a: "Yes — one script tag, same twin, same knowledge base.",
      },
    ],
  },
  {
    slug: "wholesale-distribution",
    name: "Wholesale & distribution",
    metaTitle: "AI Twin for wholesalers & distributors | Reality Twin",
    metaDescription:
      "Answer range, availability and terms questions instantly, qualify resellers before the first call, and become discoverable to buying agents across the network.",
    heroEmphasis: "the catalogue that answers back",
    intro:
      "A distributor's advantage is range and terms — both of which are usually locked in a PDF or a login. Buyers, and increasingly their agents, want an answer before they register.",
    painTitle: "Where deals leak",
    pains: [
      "Prospective resellers bounce because pricing and terms are behind a form.",
      "The same questions about minimum orders, margins and territories, every day.",
      "No idea which products buyers were actually asking about.",
    ],
    gains: [
      {
        title: "Answers range and terms questions",
        body: "Upload your category overviews, reseller terms and margin structures. The twin answers precisely what a prospective partner needs to self-qualify — without publishing your full price list unless you want to.",
      },
      {
        title: "Turns interest into a qualified partner enquiry",
        body: "When someone fits, the twin captures the full contact — company, address, phone — and files a partner enquiry with the context attached.",
      },
      {
        title: "Listed where buyers look",
        body: "Publish what you offer and what you're seeking on the Business Exchange; nightly matching surfaces counterparts, and your twin is reachable to their agents.",
      },
    ],
    queries: [
      "Which distributor carries organic skincare for the EU market?",
      "Wholesale supplier with 100-unit minimum order",
      "Who distributes this brand in Switzerland?",
    ],
    faqs: [
      {
        q: "We don't want prices public.",
        a: "Then don't index them. Many distributors publish ranges, minimums and terms, and let the twin route the pricing conversation to a human.",
      },
      {
        q: "Can it handle several brands or catalogues?",
        a: "Yes — either as one twin with all the material, or as separate twins per brand from the same account.",
      },
      {
        q: "What do we learn from it?",
        a: "Which products and terms buyers asked about, which questions went unanswered, and which agents queried your company — data that only exists because your twin is the endpoint.",
      },
    ],
  },
  {
    slug: "trades-construction",
    name: "Trades & construction",
    metaTitle: "AI Twin for trades & construction | Reality Twin",
    metaDescription:
      "Capture enquiries around the clock, qualify the job before a site visit, and book appointments straight from the conversation. Swiss-made, works for sole proprietors too.",
    heroEmphasis: "never miss the evening enquiry",
    intro:
      "Most enquiries in the trades arrive when nobody is at a desk — and the first firm to answer usually wins the job. A twin answers immediately, asks the right questions and puts the appointment in the calendar.",
    painTitle: "The everyday problem",
    pains: [
      "Enquiries arrive after hours and are answered two days later, if at all.",
      "Half the calls are jobs you don't take — wrong region, wrong scope, wrong scale.",
      "Quoting time is eaten by conversations that were never going to convert.",
    ],
    gains: [
      {
        title: "Answers immediately, at any hour",
        body: "Scope, region, materials, roughly what it costs — answered from your own material the moment someone asks, instead of the next working morning.",
      },
      {
        title: "Filters the jobs you don't want",
        body: "You define what you don't do. The twin says so politely and doesn't waste anyone's time — including yours.",
      },
      {
        title: "Books the site visit in the conversation",
        body: "When a job fits, the twin takes the name, contact details and full address, and books the appointment. You get the brief before you drive anywhere.",
      },
    ],
    queries: [
      "Who does bathroom renovations in the Zurich Oberland?",
      "Electrician available this month for a small commercial fit-out",
      "Roofer with references for heritage buildings",
    ],
    faqs: [
      {
        q: "We're a sole proprietorship without a commercial register entry. Can we still verify?",
        a: "Yes. Verification is based on proving you own your domain. The register number and VAT number are optional — a sole proprietorship in Switzerland only has to register above CHF 100'000 turnover.",
      },
      {
        q: "We don't have much of a website.",
        a: "Then upload what you have — a price list, a services PDF, or simply paste your text in. The twin works from whatever you give it, and tells you what it's missing.",
      },
      {
        q: "Can it quote binding prices?",
        a: "Not unless you explicitly allow it. By default it gives indicative figures and says the final price is confirmed after a site visit.",
      },
    ],
  },
];

// ── Second wave: the rest of the business landscape ─────────────────────────
// Same rule as above — every benefit maps to something in feature-status.ts.
INDUSTRIES.push(
  {
    slug: "real-estate",
    name: "Real estate & property",
    metaTitle: "AI Twin for real estate & property firms | Reality Twin",
    metaDescription:
      "Answer availability, viewing and tenancy questions the moment they're asked, qualify enquiries before a viewing, and book appointments in the conversation.",
    heroEmphasis: "answers while they're still interested",
    intro:
      "Property enquiries have the shortest half-life in business: an interested party who waits a day is already talking to someone else. A twin answers immediately, filters the serious enquiries and books the viewing.",
    painTitle: "Where enquiries go cold",
    pains: [
      "Enquiries arrive evenings and weekends and are answered on Monday.",
      "The same questions on availability, pets, parking, service charges and moving dates — dozens of times per listing.",
      "Viewings booked with people who were never going to qualify.",
    ],
    gains: [
      {
        title: "Answers the property questions instantly",
        body: "Upload your exposés, tenancy conditions and house rules. The twin answers on rooms, charges, availability and conditions, citing the document it came from.",
      },
      {
        title: "Qualifies before the viewing",
        body: "It collects who is asking, for when, and the constraints that matter, and hands you a brief — so a viewing slot goes to someone who fits.",
      },
      {
        title: "Books the appointment in the chat",
        body: "When someone qualifies, the twin takes their full contact details, including address, and books the viewing directly.",
      },
    ],
    queries: [
      "Which agency has 3-room apartments in Winterthur right now?",
      "Property manager for a small residential portfolio near Zurich",
      "Who handles commercial letting in the Zurich Oberland?",
    ],
    faqs: [
      {
        q: "Can it publish availability that changes daily?",
        a: "Keep the fast-moving details out of the knowledge base and let the twin route those questions to you. It re-crawls your listings on a schedule and reports what it couldn't answer.",
      },
      {
        q: "Can it agree a rent or a price?",
        a: "Not unless you explicitly allow binding statements. By default it treats figures as indicative and confirms through your team.",
      },
      {
        q: "Does it handle several properties?",
        a: "Yes. One twin can cover a whole portfolio, or you can run a twin per brand or region from the same account.",
      },
    ],
  },
  {
    slug: "hospitality",
    name: "Hospitality & tourism",
    metaTitle: "AI Twin for hotels, restaurants & tourism | Reality Twin",
    metaDescription:
      "Answer guest questions in any language, day or night, take enquiries for events and groups, and keep the answers grounded in your own information.",
    heroEmphasis: "in every language, at any hour",
    intro:
      "Guests ask in their own language, at the hour that suits them, and expect an answer before they book. A twin gives that answer from your own information — not from a review site's guess.",
    painTitle: "The service gap",
    pains: [
      "Enquiries in five languages and a team that speaks two.",
      "The same questions on opening hours, dietary options, parking, check-in and accessibility, all day long.",
      "Group and event enquiries that arrive as vague emails and take three exchanges to qualify.",
    ],
    gains: [
      {
        title: "Replies in the guest's language",
        body: "The twin answers in whatever language the guest writes in, grounded in your menus, house information and policies.",
      },
      {
        title: "Turns event enquiries into briefs",
        body: "For groups and events it collects the date, headcount, budget frame and requirements, then hands your team a brief instead of a thread.",
      },
      {
        title: "Takes bookings and requests in the conversation",
        body: "Appointment booking and paid service requests both work in chat, so an enquiry can become a confirmed slot without leaving the page.",
      },
    ],
    queries: [
      "Which hotel near Lake Zurich has a room for a small seminar?",
      "Restaurant with vegan options open on Sunday evening",
      "Venue for a 40-person company dinner in December",
    ],
    faqs: [
      {
        q: "Will it invent an opening time or a price?",
        a: "No. It answers from what you gave it and says plainly when it doesn't know, then hands over. Unanswered questions come back to you weekly.",
      },
      {
        q: "Does it replace our booking system?",
        a: "No — it answers, qualifies and captures the enquiry. Keep your booking system; the twin can hand the guest to it or take an appointment directly.",
      },
      {
        q: "How current does it stay?",
        a: "Your site is re-crawled on a schedule and every source shows its age, so seasonal information doesn't quietly go stale.",
      },
    ],
  },
  {
    slug: "healthcare",
    name: "Healthcare & practices",
    metaTitle: "AI Twin for medical & dental practices | Reality Twin",
    metaDescription:
      "Take the load off reception: answer organisational questions accurately, never give medical advice, and keep every statement on the record. Swiss company, EU-hosted data, FADP and GDPR.",
    heroEmphasis: "relieves reception, never diagnoses",
    intro:
      "A practice's phone rings all day with questions that aren't medical: hours, insurance, referrals, what to bring, where to park. A twin answers those precisely — and refuses, by design, to do anything clinical.",
    painTitle: "The reception load",
    pains: [
      "The phone is occupied by organisational questions while patients wait at the desk.",
      "New patients ask the same things about insurance, referrals and first appointments.",
      "Any AI in healthcare raises an immediate and legitimate question about liability.",
    ],
    gains: [
      {
        title: "Refusal boundaries you define",
        body: "You set explicitly what the twin must not do. It answers organisational questions and declines anything diagnostic or therapeutic, pointing to a human — that boundary is a configuration, not a hope.",
      },
      {
        title: "Every statement on the record",
        body: "The statement register keeps a timestamped, source-attributed and hash-anchored copy of every answer, exportable — which is what makes an AI defensible in a regulated setting.",
      },
      {
        title: "Swiss and EU hosting",
        body: "Data is hosted in the European Union (Ireland) under the GDPR and the Swiss FADP, with a Data Processing Addendum. Only publish what you'd publish on your website — the twin is a public-facing assistant, not a patient record system.",
      },
    ],
    queries: [
      "Which practice near me takes new patients without a referral?",
      "Dentist with Saturday hours in Zurich",
      "Physiotherapy practice that accepts supplementary insurance",
    ],
    faqs: [
      {
        q: "Can it give medical advice?",
        a: "It should not, and by default it will not. You define the refusal rules; the twin answers organisational questions and hands anything clinical to a human.",
      },
      {
        q: "Is it suitable for patient data?",
        a: "No — and don't use it that way. The knowledge base is for information you publish publicly. Patient data belongs in your practice management system.",
      },
      {
        q: "Can it book appointments?",
        a: "Yes, it can take an appointment request with contact details. Your team confirms it — the twin is not authorised to make binding commitments unless you enable that.",
      },
    ],
  },
  {
    slug: "logistics-transport",
    name: "Logistics & transport",
    metaTitle: "AI Twin for freight & transport firms | Reality Twin",
    metaDescription:
      "Answer route, capacity and equipment questions instantly, qualify freight enquiries before quoting, and become discoverable to shippers' AI agents.",
    heroEmphasis: "quotes start qualified",
    intro:
      "Freight enquiries are either precise or worthless, and sorting one from the other eats a dispatcher's day. A twin asks the qualifying questions before anything reaches your desk.",
    painTitle: "What slows dispatch",
    pains: [
      "Enquiries that omit the essentials: dimensions, weight, incoterms, dates.",
      "Repeated questions about routes, equipment, permits and customs handling.",
      "Shippers comparing three forwarders and choosing whoever answered first.",
    ],
    gains: [
      {
        title: "Collects the details a quote actually needs",
        body: "The twin asks for lane, dimensions, weight, timing and special requirements, and hands dispatch a complete brief instead of a one-line email.",
      },
      {
        title: "Answers on capabilities and coverage",
        body: "Upload your service description, equipment list and coverage. The twin answers on what you can carry, where, and under which conditions — with sources.",
      },
      {
        title: "Findable by the shipper's agent",
        body: "Your capabilities become machine-readable through the Agent Card and the MCP server, which is where procurement is starting to look for carriers.",
      },
    ],
    queries: [
      "Forwarder for temperature-controlled freight Switzerland to Italy",
      "Who handles customs clearance for e-commerce imports into the EU?",
      "Carrier with capacity for palletised freight next week",
    ],
    faqs: [
      {
        q: "Can it quote a rate?",
        a: "Only indicatively, and only if you publish rate structures. By default it collects the enquiry and lets your team price it — binding statements require explicit authorisation.",
      },
      {
        q: "Can it check live capacity?",
        a: "Not today — it works from the knowledge you give it. Keep live capacity out and let the twin route those questions to dispatch.",
      },
      {
        q: "Do we need to publish our tariffs?",
        a: "No. Most forwarders publish capabilities and coverage, and keep pricing in the human conversation.",
      },
    ],
  },
  {
    slug: "retail-ecommerce",
    name: "Retail & e-commerce",
    metaTitle: "AI Twin for retailers & online shops | Reality Twin",
    metaDescription:
      "Answer product, stock and returns questions with sources, cut repetitive support, and see what shoppers asked that you couldn't answer.",
    heroEmphasis: "answers that cite your own pages",
    intro:
      "Most shop questions are the same twenty, and most shoppers won't email to ask them — they leave. A twin answers from your own product and policy pages, in the moment.",
    painTitle: "Where carts are lost",
    pains: [
      "Questions about sizing, materials, delivery times and returns go unanswered at the decisive moment.",
      "Support handles the same enquiries every day.",
      "No visibility into what shoppers wanted to know but never asked.",
    ],
    gains: [
      {
        title: "Grounded product answers",
        body: "The twin answers from your product descriptions, size guides and policies — with the source attached, so the answer is checkable rather than plausible.",
      },
      {
        title: "Shows you the questions you're not answering",
        body: "Every question it couldn't answer is reported. That list is the most honest product-content roadmap you'll get, written by your own customers.",
      },
      {
        title: "Captures the ones worth following up",
        body: "For B2B and wholesale enquiries it takes the full contact — company, address, phone — and files it with the context.",
      },
    ],
    queries: [
      "Does this shop deliver to Austria and how long does it take?",
      "Where can I buy this brand in Switzerland?",
      "Shop with a 30-day return policy for outdoor equipment",
    ],
    faqs: [
      {
        q: "Does it know live stock levels?",
        a: "No. It works from indexed content, so keep live stock in your shop system and let the twin answer the questions around the purchase.",
      },
      {
        q: "Will it invent a delivery time?",
        a: "No — it answers from your published policy, and treats any figure as indicative unless you've authorised binding statements.",
      },
      {
        q: "Can we use it for B2B enquiries too?",
        a: "Yes. The in-chat contact form captures company, address and phone, which is what a wholesale enquiry needs.",
      },
    ],
  },
  {
    slug: "education-training",
    name: "Education & training",
    metaTitle: "AI Twin for schools & training providers | Reality Twin",
    metaDescription:
      "Answer prospective students' questions about courses, entry requirements, dates and costs — in their language, with the sources they can verify.",
    heroEmphasis: "answers before the enrolment deadline",
    intro:
      "Prospective students compare programmes at midnight and decide within days. The provider whose information is clear, current and instantly available is the one that gets the application.",
    painTitle: "What costs enrolments",
    pains: [
      "The same questions about entry requirements, dates, recognition and costs, from every prospective student.",
      "Information spread across PDFs, a course catalogue and a website that disagree with each other.",
      "Enquiries in several languages during peak enrolment weeks.",
    ],
    gains: [
      {
        title: "One consistent answer",
        body: "Index your course descriptions, regulations and schedules. The contradiction check flags where two of your own sources disagree — before a prospective student finds it.",
      },
      {
        title: "Answers in the student's language",
        body: "The twin replies in whatever language it's asked in, from the same underlying material.",
      },
      {
        title: "Turns interest into a qualified enquiry",
        body: "It captures who is asking, which programme and when, and can book an advisory appointment straight from the conversation.",
      },
    ],
    queries: [
      "Which part-time courses in project management start in autumn?",
      "Recognised further education in Switzerland for career changers",
      "What are the entry requirements for this programme?",
    ],
    faqs: [
      {
        q: "Our regulations are long PDFs. Can it use them?",
        a: "Yes — upload them. The twin answers from them and cites the document, which is exactly what a prospective student needs to trust the answer.",
      },
      {
        q: "Can it state fees?",
        a: "It can, if you publish them. As with any figure, it's presented as indicative unless you explicitly authorise binding statements.",
      },
      {
        q: "How do we know what applicants really want to know?",
        a: "The weekly gap report lists the questions it couldn't answer — usually the fastest way to improve a course page.",
      },
    ],
  },
  {
    slug: "finance-insurance",
    name: "Finance & insurance",
    metaTitle: "AI Twin for brokers & financial advisors | Reality Twin",
    metaDescription:
      "Answer product and process questions accurately, keep every statement auditable and non-binding by default, and qualify enquiries before the first meeting.",
    heroEmphasis: "auditable by design",
    intro:
      "In finance the risk isn't that an AI says too little — it's that it says something binding. A twin that is non-binding by default and keeps a record of every statement changes that calculation.",
    painTitle: "Why most firms hesitate",
    pains: [
      "A real fear that an assistant states a rate, a coverage or a deadline that later has to be corrected.",
      "Expensive advisors spending first meetings on questions a document could answer.",
      "No record of what a digital channel told a prospect.",
    ],
    gains: [
      {
        title: "Non-binding unless you say otherwise",
        body: "Commitment levels are built in: every answer is recorded as informational, indicative or binding, and binding is impossible unless the owner explicitly enables it.",
      },
      {
        title: "A defensible record",
        body: 'The statement register keeps every answer timestamped, with its sources and a tamper-evident hash, and exports as a file — so "what did it tell them" has an answer.',
      },
      {
        title: "Qualifies before the meeting",
        body: "The twin collects the situation, timeframe and scope and books the appointment with a brief attached.",
      },
    ],
    queries: [
      "Which broker advises SMEs on occupational pension plans?",
      "Trustee for a Swiss GmbH with cross-border employees",
      "Independent advisor for company insurance in the Zurich region",
    ],
    faqs: [
      {
        q: "Can it give financial advice?",
        a: "It answers from your published material and is instructed to hand over anything requiring a professional assessment. You set those boundaries explicitly.",
      },
      {
        q: "How do we prove what was said?",
        a: "Export the statement register: every answer with its timestamp, sources and commitment level.",
      },
      {
        q: "Where is the data?",
        a: "The European Union (Ireland), under the GDPR and the Swiss FADP. Reality Twin itself is a Swiss company governed by Swiss law, and a Data Processing Addendum is available.",
      },
    ],
  },
  {
    slug: "associations-nonprofits",
    name: "Associations & non-profits",
    metaTitle: "AI Twin for associations & non-profits | Reality Twin",
    metaDescription:
      "Answer member and donor questions around the clock without adding staff, and keep your published information consistent across every channel.",
    heroEmphasis: "capacity you don't have to hire",
    intro:
      "Associations answer the same questions with a fraction of the staff a company would have. A twin absorbs the repetitive part so the people are free for the work that needs them.",
    painTitle: "The capacity problem",
    pains: [
      "A small team answering membership, event and statute questions by email.",
      "Information scattered across statutes, newsletters and a website nobody has time to maintain.",
      "Enquiries arriving outside the two days a week the office is staffed.",
    ],
    gains: [
      {
        title: "Answers members without adding headcount",
        body: "Index your statutes, membership conditions and event information. The twin answers with the source attached, at any hour.",
      },
      {
        title: "Keeps your own information honest",
        body: "Freshness scoring shows which sources have gone stale, and the contradiction check finds places where two of your documents disagree.",
      },
      {
        title: "Routes what needs a person",
        body: "When something needs a human — a membership decision, a sensitive request — it says so and captures the contact with context.",
      },
    ],
    queries: [
      "How do I become a member of this association?",
      "Which foundation supports projects in this field?",
      "What are the statutes on voting rights?",
    ],
    faqs: [
      {
        q: "We have almost no budget.",
        a: "Start on the free tier: one twin, your website indexed, a public profile. You'll know within a month whether it takes work off the team.",
      },
      {
        q: "Can it handle our statutes?",
        a: "Yes — upload the PDF. It answers from it and cites the passage, which matters when the answer is a rule rather than an opinion.",
      },
      {
        q: "Can it commit us to anything?",
        a: "No. Binding statements are off unless explicitly enabled, so it gives information and refers decisions to your board or office.",
      },
    ],
  },
);

export const INDUSTRY_BY_SLUG: Record<string, Industry> = Object.fromEntries(
  INDUSTRIES.map((i) => [i.slug, i]),
);
